import { ArrowRightIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { Button, Modal } from 'ui-toolkit-tailwind/src/components'

import HoldToConfirmButton from './HoldToConfirmButton'

const formatTimestamp = value => {
	if (!value) return 'Not recorded'

	return new Intl.DateTimeFormat('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		second: '2-digit',
		timeZoneName: 'short'
	}).format(new Date(value))
}

const latestRunLabel = status => {
	switch (status) {
	case 'success':
		return 'Success'
	case 'failed':
		return 'Failed'
	default:
		return 'Not recorded'
	}
}

const detailRows = details => [
	{
		label: 'Last successful email send',
		value: formatTimestamp(details?.lastSuccessfulEmailSentAt)
	},
	{
		label: 'Next scheduled email send',
		value: formatTimestamp(details?.nextScheduledSendAt)
	},
	{
		label: 'Most recent scheduled run',
		value: latestRunLabel(details?.latestScheduledRun?.status)
	}
]

const ManualEmailSendModal = ({ visible, details, activeAction, onClose, onSend, onSendAndNotify }) => (
	<Modal
		visible={visible}
		onClose={activeAction ? () => {} : onClose}
		size="2xl"
		panelClassName="sm:max-w-[40rem]"
		clickOutsideToClose={!activeAction}
	>
		<Modal.Header>Email Manually</Modal.Header>
		<Modal.Body>
			<div className="space-y-5">
				<p className="text-sm text-gray-600">
					This action queues available reports and corrects earlier reports with incomplete data. Reports awaiting complete VGT data are held and checked every five minutes.
				</p>

				<div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
					{detailRows(details).map(row => (
						<div key={row.label} className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
							<p className="text-xs font-semibold uppercase tracking-wide text-amber-900">{row.label}</p>
							<p className="text-sm text-gray-700 sm:max-w-[15rem] sm:text-right">{row.value}</p>
						</div>
					))}

					{details?.latestScheduledRun?.status === 'failed' && (
						<div className="border-t border-amber-200 pt-3">
							<p className="text-xs font-semibold uppercase tracking-wide text-amber-900">Failure reason</p>
							<p className="mt-1 text-sm text-gray-700">
								{details.latestScheduledRun.failureReason || 'No failure reason was recorded.'}
							</p>
						</div>
					)}
				</div>

				<p className="text-xs text-gray-500">
					Send Emails &amp; Notify Admin also queues a separate admin summary. An admin notification does not confirm report delivery.
				</p>
			</div>
		</Modal.Body>
		<Modal.Footer>
			<div className="flex flex-col-reverse gap-3 px-4 py-5 sm:flex-row sm:items-start sm:justify-end sm:px-6">
				<Button
					type="button"
					intent="secondary"
					onClick={onClose}
					disabled={!!activeAction}
					className="inline-flex w-full items-center justify-center px-4 py-2 text-sm sm:w-[11.5rem]"
				>
					Cancel
				</Button>
				<HoldToConfirmButton
					id="manual-email-send"
					label="Send Emails"
					loadingLabel="Sending..."
					holdLabel="Keep Holding..."
					helperText="Press and hold to send."
					holdingHelperText="Keep holding to send the emails."
					busy={activeAction === 'send'}
					disabled={!!activeAction && activeAction !== 'send'}
					onConfirm={onSend}
					className="min-h-[38px] bg-[#3E63F4] text-white hover:bg-[#0b2986] focus-visible:outline-[#3E63F4]"
					progressClassName="bg-[#0b2986]/90"
					icon={ArrowRightIcon}
					wrapperClassName="sm:w-[11.5rem]"
				/>
				<HoldToConfirmButton
					id="manual-email-send-notify"
					label="Send Emails & Notify Admin"
					loadingLabel="Sending..."
					holdLabel="Keep Holding..."
					helperText="Press and hold to send and notify."
					holdingHelperText="Keep holding to send and notify the administrator."
					busy={activeAction === 'send_and_notify'}
					disabled={!!activeAction && activeAction !== 'send_and_notify'}
					onConfirm={onSendAndNotify}
					className="min-h-[38px] bg-[#0ea62d] text-white hover:bg-[#05721b] focus-visible:outline-[#0ea62d]"
					progressClassName="bg-[#05721b]/90"
					icon={ArrowRightIcon}
					wrapperClassName="sm:w-[11.5rem]"
				/>
			</div>
		</Modal.Footer>
	</Modal>
)

ManualEmailSendModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	details: PropTypes.shape({
		lastSuccessfulEmailSentAt: PropTypes.string,
		nextScheduledSendAt: PropTypes.string,
		latestScheduledRun: PropTypes.shape({
			status: PropTypes.string,
			failureReason: PropTypes.string
		})
	}),
	activeAction: PropTypes.oneOf([null, 'send', 'send_and_notify']),
	onClose: PropTypes.func.isRequired,
	onSend: PropTypes.func.isRequired,
	onSendAndNotify: PropTypes.func.isRequired
}

ManualEmailSendModal.defaultProps = {
	details: null
}

export default ManualEmailSendModal
